import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "./AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Save } from "lucide-react";

const fields = {
  contact: [
    ["whatsapp_number", "WhatsApp Number"],
    ["contact_email", "Contact Email"],
    ["contact_phone", "Contact Phone"],
    ["address", "Address"],
  ],
  social: [
    ["facebook", "Facebook URL"],
    ["instagram", "Instagram URL"],
    ["twitter", "Twitter / X URL"],
    ["telegram", "Telegram URL"],
  ],
};

export default function AdminSettings() {
  const qc = useQueryClient();
  const { user, refresh } = useAuth();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [form, setForm] = useState({});
  const [profile, setProfile] = useState({ name: "", password: "" });

  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  useEffect(() => { if (user) setProfile((p) => ({ ...p, name: user.name })); }, [user]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/admin/settings", form)).data,
    onSuccess: () => { toast.success("Settings saved"); qc.invalidateQueries({ queryKey: ["settings"] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const saveProfile = useMutation({
    mutationFn: async () => (await api.put("/auth/profile", profile)).data,
    onSuccess: () => { toast.success("Profile updated"); refresh(); setProfile((p) => ({ ...p, password: "" })); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div>
      <PageHeader title="Settings" subtitle="Configure contact info, socials, payments and your profile" />
      <Tabs defaultValue="contact">
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="contact" data-testid="settings-tab-contact">Contact &amp; Social</TabsTrigger>
          <TabsTrigger value="payment" data-testid="settings-tab-payment">Payment</TabsTrigger>
          <TabsTrigger value="profile" data-testid="settings-tab-profile">Admin Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="contact" className="mt-6">
          <div className="glass rounded-2xl p-6 max-w-2xl grid sm:grid-cols-2 gap-4">
            {[...fields.contact, ...fields.social].map(([k, label]) => (
              <div key={k}>
                <Label className="text-slate-300">{label}</Label>
                <Input data-testid={`settings-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white" />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Button data-testid="settings-save" onClick={() => save.mutate()} disabled={save.isPending} className="glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0"><Save className="w-4 h-4 mr-1" /> Save Settings</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="payment" className="mt-6">
          <div className="glass rounded-2xl p-6 max-w-2xl space-y-4">
            <div>
              <Label className="text-slate-300">Payment Instructions (shown at checkout)</Label>
              <Textarea data-testid="settings-payment-instructions" rows={4} value={form.payment_instructions || ""} onChange={(e) => set("payment_instructions", e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Bank / Payment Details</Label>
              <Textarea data-testid="settings-bank-details" rows={3} value={form.bank_details || ""} onChange={(e) => set("bank_details", e.target.value)} className="mt-1 bg-white/5 border-white/10 text-white" />
            </div>
            <Button data-testid="settings-payment-save" onClick={() => save.mutate()} disabled={save.isPending} className="glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0"><Save className="w-4 h-4 mr-1" /> Save</Button>
          </div>
        </TabsContent>

        <TabsContent value="profile" className="mt-6">
          <div className="glass rounded-2xl p-6 max-w-lg space-y-4">
            <div><Label className="text-slate-300">Email</Label><Input value={user?.email || ""} disabled className="mt-1 bg-white/5 border-white/10 text-slate-500" /></div>
            <div><Label className="text-slate-300">Name</Label><Input data-testid="admin-profile-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-slate-300">New Password (leave blank to keep)</Label><Input data-testid="admin-profile-password" type="password" value={profile.password} onChange={(e) => setProfile({ ...profile, password: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <Button data-testid="admin-profile-save" onClick={() => saveProfile.mutate()} disabled={saveProfile.isPending} className="glow-btn bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0"><Save className="w-4 h-4 mr-1" /> Update Profile</Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
